import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api } from '../api/client'
import type { AlarmInbox as AlarmInboxData, AlarmInboxEntry } from '../api/types'
import { alarmUnchecked, bellBadge, bellLabel, closedWording } from '../lib/alarmInbox'
import { levelWord } from '../lib/alerts'
import { formatDateTime } from '../lib/time'
import type { AsyncState } from '../lib/useAsync'
import { usePolled } from '../lib/usePolled'

const NOTE_MAX = 500
const POLL_MS = 30_000

/** The bell in the utility bar and the person's alarm inbox behind it: the
 *  events on rules that tell them. Marking one checked clears it from their
 *  own list; adding what is being done makes it an acknowledgement everyone
 *  sees on the alarm (Next Revision, spec 7.2). */
export function AlarmInbox() {
  const [before, setBefore] = useState<number | null>(null)
  const inbox = usePolled(() => api.alarmInbox.list(before), [before], POLL_MS)
  const [open, setOpen] = useState(false)
  // The link in an escalation email (/overview?inbox=1) opens the inbox, then the address is cleaned.
  const [params, setParams] = useSearchParams()
  useEffect(() => {
    if (!params.has('inbox')) return
    setBefore(null); setOpen(true)
    const rest = new URLSearchParams(params); rest.delete('inbox')
    setParams(rest, { replace: true })
  }, [params, setParams])
  const badge = bellBadge(inbox.data, Boolean(inbox.error))
  return <>
    <button type="button" className="ws-alarm-bell" aria-label={bellLabel(inbox.data, Boolean(inbox.error))}
      onClick={() => { setBefore(null); setOpen(true) }}>
      <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <path d="M5 17h14l-2-3V9a5 5 0 0 0-10 0v5zM10 20h4M12 2v2" /></svg>
      {badge && <span className="ws-alarm-badge">{badge}</span>}
    </button>
    {open && <InboxDialog inbox={inbox} before={before} onPage={setBefore} onClose={() => setOpen(false)} />}
  </>
}

function InboxDialog({ inbox, before, onPage, onClose }: {
  inbox: AsyncState<AlarmInboxData>; before: number | null; onPage: (before: number | null) => void; onClose: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => { dialog.current?.showModal() }, [])
  const data = inbox.data
  return <dialog ref={dialog} className="alarm-inbox" aria-labelledby="alarm-inbox-title" onCancel={event => { event.preventDefault(); onClose() }}>
    <header><h2 id="alarm-inbox-title">Alarm notifications</h2>
      <button type="button" className="alarm-inbox-close" aria-label="Close" onClick={onClose}>×</button></header>
    <p className="alarm-inbox-help">The alarms on rules that tell you. Marking one checked clears it from your list only.
      Say what is being done and everyone sees it on the alarm.</p>
    {inbox.error && <p role="alert" className="error">{inbox.error} <button type="button" className="button" onClick={inbox.reload}>Retry</button></p>}
    {inbox.loading && !data && <p role="status">Loading notifications…</p>}
    {data && !data.active.length && !data.closed.length && <p>No unchecked or active alarms.</p>}
    {data && <>
      {data.active.length > 0 && <section aria-label="Active alarms"><h3>Active</h3>
        {data.active.map(entry => <Entry key={entry.id} entry={entry} onChecked={inbox.reload} onNavigate={onClose} />)}</section>}
      {data.closed.length > 0 && <section aria-label="Ended alarms"><h3>Ended, not yet checked</h3>
        {data.closed.map(entry => <Entry key={entry.id} entry={entry} onChecked={inbox.reload} onNavigate={onClose} />)}</section>}
      <nav aria-label="Alarm inbox pages" className="alarm-inbox-pages">
        <span>{data.unchecked} to check · {data.total} in your inbox · 100 ended alarms a page</span>
        {before !== null && <button type="button" className="button" onClick={() => onPage(null)}>Newest</button>}
        {data.next_before !== null && <button type="button" className="button" disabled={inbox.loading}
          onClick={() => onPage(data.next_before)}>Older</button>}
      </nav></>}
  </dialog>
}

function Entry({ entry, onChecked, onNavigate }: { entry: AlarmInboxEntry; onChecked: () => void; onNavigate: () => void }) {
  const [asking, setAsking] = useState(false)
  const [note, setNote] = useState('')
  const noted = Boolean(entry.note)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const unchecked = alarmUnchecked(entry)
  const active = !entry.closed_at
  // Someone who only cleared their bell can still say what is being done: that note,
  // not the check, is what everyone sees and what stops the escalation emails.
  const reword = active && !unchecked
  const ask = () => { setNote(reword ? entry.note ?? '' : ''); setAsking(true) }
  const send = async (text?: string) => {
    setBusy(true); setError('')
    try { await api.alarmInbox.check(entry.id, text); setAsking(false); setNote(''); onChecked() }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Could not mark the alarm checked.') }
    finally { setBusy(false) }
  }
  const submit = (event: FormEvent) => { event.preventDefault(); void send(note) }
  return <article className="alarm-inbox-entry">
    <div className="alarm-inbox-head"><strong>{entry.name}</strong>
      <span className={`alarm-level alarm-level-${entry.level}`}><span className="al-dot" />{levelWord(entry.level)}</span></div>
    <p className="alarm-inbox-where">{entry.station ?? entry.project} · {formatDateTime(entry.opened_at)} · {entry.closed_at
      ? `${closedWording(entry.closed_reason)} ${formatDateTime(entry.closed_at)}` : 'Active'}</p>
    {entry.note && <p className="alarm-inbox-note"><strong>You noted:</strong> {entry.note}</p>}
    {error && <p role="alert" className="error">{error}</p>}
    {asking
      ? <form className="alarm-inbox-form" onSubmit={submit}>
        <label>What is being done?{reword ? '' : <> <small>(optional)</small></>}
          <textarea rows={2} maxLength={NOTE_MAX} autoFocus disabled={busy} value={note} onChange={event => setNote(event.target.value)} /></label>
        <small>{note.trim() ? 'Everyone will see this on the alarm.' : 'With no note this clears the alarm from your list only.'}</small>
        <div className="alarm-inbox-actions">
          <button type="submit" className="button primary" disabled={busy || (reword && !note.trim())}>{busy ? 'Saving…' : reword ? 'Save' : 'Mark checked'}</button>
          <button type="button" className="button" disabled={busy} onClick={() => setAsking(false)}>Cancel</button></div>
      </form>
      : <div className="alarm-inbox-actions">
        <Link to="/alerts?tab=events" onClick={onNavigate}>View alarms</Link>
        {(unchecked || reword) && <button type="button" className="button" disabled={busy}
          onClick={() => active ? ask() : void send()}>{unchecked ? 'Mark checked' : noted ? 'Change what is being done' : 'Add what is being done'}</button>}</div>}
  </article>
}
